import Link from "next/link";

import { ArrowRight } from "lucide-react";

import { cn } from "@louez/utils";

import type { PortMegaMenuConfig } from "@/components/port-portal/util.port-mega-menu";

type PortMegaMenuPanelProps = {
  menu: PortMegaMenuConfig;
};

const isExternalHref = (href: string) => href.startsWith("http");

export const PortMegaMenuPanel = ({ menu }: PortMegaMenuPanelProps) => (
  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)]">
    <div className="grid gap-0 lg:grid-cols-[1.15fr_1.15fr_0.95fr_0.95fr]">
      {menu.columns.map((column) => (
        <div
          key={column.heading}
          className="border-b border-slate-100 p-6 lg:border-r lg:border-b-0"
        >
          <p className="text-[11px] font-semibold tracking-[0.18em] text-slate-400 uppercase">
            {column.heading}
          </p>
          <ul className="mt-5 space-y-5">
            {column.links.map((link) => {
              const className =
                "group block rounded-xl transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500";
              const content = (
                <>
                  <span className="block text-sm font-semibold text-slate-950 group-hover:text-sky-900">
                    {link.title}
                  </span>
                  <span className="mt-1 block text-sm leading-5 text-slate-500">
                    {link.description}
                  </span>
                </>
              );

              return (
                <li key={link.title}>
                  {isExternalHref(link.href) ? (
                    <Link href={link.href} className={cn(className, "-m-2 p-2")}>
                      {content}
                    </Link>
                  ) : (
                    <a href={link.href} className={cn(className, "-m-2 p-2")}>
                      {content}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <div className="border-b border-slate-100 p-6 lg:border-r lg:border-b-0">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-slate-400 uppercase">
          {menu.featured.heading}
        </p>
        <div className="mt-5 space-y-3">
          {menu.featured.items.map((item) => {
            const card = (
              <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 p-3 transition-colors group-hover:bg-slate-100">
                <div>
                  <p className="text-sm font-semibold text-slate-950">{item.title}</p>
                  <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-sky-700">
                    ver más
                    <ArrowRight className="size-3" />
                  </span>
                </div>
                <img
                  src={item.imageUrl}
                  alt=""
                  className="h-14 w-16 shrink-0 rounded-xl object-cover"
                />
              </div>
            );

            return isExternalHref(item.href) ? (
              <Link key={item.title} href={item.href} className="group block">
                {card}
              </Link>
            ) : (
              <a key={item.title} href={item.href} className="group block">
                {card}
              </a>
            );
          })}
        </div>
      </div>

      <div className="bg-gradient-to-b from-sky-50 via-white to-rose-50 p-6">
        <div className="flex h-full flex-col rounded-2xl">
          <h3 className="text-2xl leading-tight font-bold tracking-tight text-slate-950">
            {menu.promo.title}
          </h3>
          <p className="mt-4 text-sm leading-6 text-slate-600">{menu.promo.description}</p>
          {isExternalHref(menu.promo.href) ? (
            <Link
              href={menu.promo.href}
              className="mt-auto inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              {menu.promo.cta}
              <ArrowRight className="size-4" />
            </Link>
          ) : (
            <a
              href={menu.promo.href}
              className="mt-auto inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              {menu.promo.cta}
              <ArrowRight className="size-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  </div>
);
